import { ReactNode, useState } from "react";
import { Modal, Segmented } from "antd";
import { Box, Typography } from "@mui/material";
import dayjs from "dayjs";
import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_TERTIARY } from "../../constants";

export interface InfoSectionI {
  title: string;
  fields: { key: string; label: string }[];
}

const formatValue = (v: any): string => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Sí" : "No";
  if (
    typeof v === "string" &&
    /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2})?/.test(v) &&
    dayjs(v).isValid()
  ) {
    return dayjs(v).format("DD/MM/YYYY HH:mm");
  }
  return String(v);
};

interface ContainerDetailModalI {
  open: boolean;
  onClose: () => void;
  isLoading: boolean;
  kind: string;
  container: string;
  data: Record<string, any>;
  sections: InfoSectionI[];
  gallery?: ReactNode;
  prefix?: string;
}

export const ContainerDetailModal = ({
  open, onClose, isLoading, kind, container, data, sections, gallery, prefix = "N° ",
}: ContainerDetailModalI) => {
  const [selected, setView] = useState<"fotos" | "info">("fotos");
  const view = gallery ? selected : "info";
  const contentSx = { height: "min(550px, 65vh)", overflowY: "auto", p: 1.5 } as const;

  return (
    <Modal
      title={
        <Box sx={{ pr: 3 }}>
          <Typography sx={{ color: COLOR_SECONDARY, fontSize: 12, letterSpacing: 1 }}>
            {kind}
          </Typography>
          <Typography
            sx={{ color: COLOR_TERTIARY, fontWeight: 800, fontSize: { xs: 16, sm: 20 }, wordBreak: "break-all" }}
          >
            {prefix}{container}
          </Typography>
        </Box>
      }
      footer={null}
      loading={isLoading}
      open={open}
      onCancel={onClose}
      width="min(900px, 96vw)"
      centered
      zIndex={1602}
      destroyOnClose
      styles={{
        header: { borderBottom: `4px solid ${COLOR_PRIMARY}`, paddingBottom: 12, marginBottom: 0 },
        body: { padding: 0 },
      }}
    >
      {gallery ? (
      <Box sx={{ display: "flex", justifyContent: "center", py: 1.5, px: 1 }}>
        <style>{`
          .sb-segmented.ant-segmented { background: ${COLOR_TERTIARY}14; padding: 4px; }
          .sb-segmented .ant-segmented-item { color: ${COLOR_TERTIARY}; font-weight: 600; }
          .sb-segmented .ant-segmented-item-selected,
          .sb-segmented .ant-segmented-thumb { background: ${COLOR_PRIMARY} !important; }
          .sb-segmented .ant-segmented-item-selected,
          .sb-segmented .ant-segmented-item-selected .ant-segmented-item-label,
          .sb-segmented .ant-segmented-item:has(input:checked),
          .sb-segmented .ant-segmented-item:has(input:checked) .ant-segmented-item-label { color: #fff !important; }
        `}</style>
        <Segmented
          className="sb-segmented"
          size="large"
          block
          style={{ width: "min(420px, 100%)" }}
          value={view}
          onChange={(v) => setView(v as "fotos" | "info")}
          options={[
            { label: "Fotos", value: "fotos" },
            { label: "Información", value: "info" },
          ]}
        />
      </Box>
      ) : null}

      {view === "fotos" ? (
        <Box
          sx={{
            ...contentSx,
            display: "grid",
            gap: 2,
            gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 220px), 1fr))",
          }}
        >
          {gallery}
        </Box>
      ) : (
        <Box sx={{ ...contentSx, display: "grid", gap: 2, alignContent: "start", gridAutoRows: "max-content" }}>
          {sections.map((sec) => (
            <Box
              key={sec.title}
              sx={{ border: `1px solid ${COLOR_PRIMARY}`, borderRadius: 2, overflow: "hidden", minHeight: "min-content" }}
            >
              <Box
                sx={{
                  background: COLOR_SECONDARY, color: "#fff", px: 2, py: 0.75,
                  fontWeight: 700, borderLeft: `6px solid ${COLOR_PRIMARY}`,
                }}
              >
                {sec.title}
              </Box>
              <Box sx={{ display: "grid", gridAutoRows: "max-content", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" } }}>
                {sec.fields.map((f) => (
                  <Box key={f.key} sx={{ px: 2, py: 1, borderBottom: `1px solid ${COLOR_PRIMARY}44` }}>
                    <Typography
                      sx={{ color: COLOR_SECONDARY, fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}
                    >
                      {f.label}
                    </Typography>
                    <Typography sx={{ color: COLOR_TERTIARY, fontSize: 14, wordBreak: "break-word" }}>
                      {formatValue(data[f.key])}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Modal>
  );
};
